import _classPrivateFieldGet from '@babel/runtime/helpers/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/classPrivateFieldSet';
import { setAttribute, dispatchEvent, toArray, ElementIntersectionObserver } from '@nrk/dh-basis-common';
import { ViewportPositionMixin, AttributeBindingMixin, VIEWPORT_POSITION_ATTR } from '@nrk/dh-basis-mixins';

var VIEWPORT_POSITIONS = {
  aboveViewport: 'ABOVE-VIEWPORT',
  inViewport: 'IN-VIEWPORT',
  belowViewport: 'BELOW-VIEWPORT'
};

function _classPrivateMethodGet(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }
/* Attributes */
// VIEWPORT_POSITION_ATTR – Added by ViewportPositionMixin

var ACTIVE_BACKGROUND_ATTR = 'active-background-id';
var ACTIVE_LAYER_IDS_ATTR = 'active-layer-ids';
var ROOT_MARGIN_ATTR = 'root-margin';
/* Child attributes */

var BG_ID_ATTR = 'data-background-id';
var SHOW_BACKGROUND_ATTR = 'data-show-background';
var LAYER_ID_ATTR = 'data-layer-id';
var SHOW_LAYERS_ATTR = 'data-show-layers';
var ATTRIBUTE_NAME = 'data-attribute-name';
var ATTRIBUTE_VALUE = 'data-attribute-value';
var STATE_ATTR = 'data-state';
var VIEWPORT_POSITION_DATA_ATTR = 'data-viewport-position';
/* Events */

var TRIGGER_ACTIVATED_EVENT = 'dh-basis-scrollhistorie-trigger-activated';

function isTrigger(elm) {
  return elm.hasAttribute(SHOW_BACKGROUND_ATTR);
}

function isBackground(elm) {
  return elm.hasAttribute(BG_ID_ATTR);
}

function isLayer(elm) {
  return elm.hasAttribute(LAYER_ID_ATTR);
}
/**
 * Custom element that manages a set of background, trigger and layer elements.
 *
 * Background elements are annotated with a `data-background-id` attribute.
 *
 * Trigger elements are annotated with a `data-show-background` attribute,
 * and optional `data-show-layers`, `data-attribute-name` and `data-attribute-value` attributes.
 *
 * Layer elements are annotated with a `data-layer-id` attribute.
 *
 * When a trigger element comes into view it will set the `data-state` attribute
 * of the corresponding background to `ACTIVE`.
 * The previous and next background will get `data-state`=`PREV` and `NEXT`.
 */


var _activeTriggerIndex = /*#__PURE__*/new WeakMap();

var _observer = /*#__PURE__*/new WeakMap();

var _createIntersectionObserver = /*#__PURE__*/new WeakSet();

var _disconnectIntersectionObserver = /*#__PURE__*/new WeakSet();

var _handleTriggerChange = /*#__PURE__*/new WeakMap();

var _prepareBackgrounds = /*#__PURE__*/new WeakSet();

var _updateBackgrounds = /*#__PURE__*/new WeakSet();

var _getBackgroundId = /*#__PURE__*/new WeakSet();

var _updateLayers = /*#__PURE__*/new WeakSet();

class Scrollhistorie extends ViewportPositionMixin(AttributeBindingMixin(HTMLElement)) {
  constructor() {
    super(...arguments);

    _updateLayers.add(this);

    _getBackgroundId.add(this);

    _updateBackgrounds.add(this);

    _prepareBackgrounds.add(this);

    _disconnectIntersectionObserver.add(this);

    _createIntersectionObserver.add(this);

    _activeTriggerIndex.set(this, {
      writable: true,
      value: void 0
    });

    _observer.set(this, {
      writable: true,
      value: void 0
    });

    _handleTriggerChange.set(this, {
      writable: true,
      value: intersections => {
        var activeIntersection = intersections.filter(i => i.isIntersecting).pop();

        if (!activeIntersection || !activeIntersection.target) {
          _classPrivateFieldSet(this, _activeTriggerIndex, null);

          return;
        }

        var triggerElement = activeIntersection.target; // Set active background

        this.activeBackgroundId = triggerElement.getAttribute(SHOW_BACKGROUND_ATTR); // Update attribute on active background

        var attributeName = triggerElement.getAttribute(ATTRIBUTE_NAME);
        var attributeValue = triggerElement.getAttribute(ATTRIBUTE_VALUE);

        if (attributeName) {
          var _this$activeBackgroun;

          (_this$activeBackgroun = this.activeBackground) === null || _this$activeBackgroun === void 0 ? void 0 : _this$activeBackgroun.setAttribute(attributeName, attributeValue);
        }

        this.triggerElements.forEach(elm => setAttribute(elm, STATE_ATTR, elm === triggerElement ? 'ACTIVE' : undefined));
        this.activeLayerIds = triggerElement.getAttribute(SHOW_LAYERS_ATTR);
        var triggerIndex = this.triggerElements.indexOf(triggerElement);

        if (triggerIndex !== _classPrivateFieldGet(this, _activeTriggerIndex)) {
          _classPrivateFieldSet(this, _activeTriggerIndex, triggerIndex);

          dispatchEvent(this, TRIGGER_ACTIVATED_EVENT, {
            triggerIndex,
            backgroundId: this.activeBackgroundId,
            attributeName,
            attributeValue
          });
        }
      }
    });
  }

  static get observedAttributes() {
    return [...super.observedAttributes, ROOT_MARGIN_ATTR, ACTIVE_BACKGROUND_ATTR, ACTIVE_LAYER_IDS_ATTR];
  } // Binding VIEWPORT-POSITION attribute to background DATA-VIEWPORT-POSITION attribute


  static get boundAttributes() {
    return [{
      attribute: "".concat(VIEWPORT_POSITION_ATTR, ":").concat(VIEWPORT_POSITION_DATA_ATTR),
      targets: instance => instance.backgrounds
    }];
  }

  connectedCallback() {
    super.connectedCallback();

    _classPrivateMethodGet(this, _updateBackgrounds, _updateBackgrounds2).call(this);

    _classPrivateMethodGet(this, _createIntersectionObserver, _createIntersectionObserver2).call(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    _classPrivateMethodGet(this, _disconnectIntersectionObserver, _disconnectIntersectionObserver2).call(this);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === ROOT_MARGIN_ATTR) {
      _classPrivateMethodGet(this, _disconnectIntersectionObserver, _disconnectIntersectionObserver2).call(this);

      _classPrivateMethodGet(this, _createIntersectionObserver, _createIntersectionObserver2).call(this);
    } else if (name === VIEWPORT_POSITION_ATTR) {
      _classPrivateMethodGet(this, _prepareBackgrounds, _prepareBackgrounds2).call(this);

      _classPrivateMethodGet(this, _updateBackgrounds, _updateBackgrounds2).call(this);
    } else if (name === ACTIVE_BACKGROUND_ATTR) {
      _classPrivateMethodGet(this, _updateBackgrounds, _updateBackgrounds2).call(this);
    } else if (name === ACTIVE_LAYER_IDS_ATTR) {
      _classPrivateMethodGet(this, _updateLayers, _updateLayers2).call(this);
    }
  }

  get triggerElements() {
    return toArray(this.children).filter(isTrigger);
  }

  get backgrounds() {
    return toArray(this.children).filter(isBackground);
  }

  get layers() {
    return toArray(this.children).filter(isLayer);
  }
  /* Mirrored attributes: */


  get rootMargin() {
    return this.getAttribute(ROOT_MARGIN_ATTR) || '0px 0px 0px 0px';
  }

  set rootMargin(val) {
    this.setAttribute(ROOT_MARGIN_ATTR, val || '0px 0px 0px 0px');
  }

  set activeBackgroundId(value) {
    if (value !== this.activeBackgroundId) {
      this.setAttribute(ACTIVE_BACKGROUND_ATTR, value);
    }
  }

  get activeBackgroundId() {
    return this.getAttribute(ACTIVE_BACKGROUND_ATTR);
  }

  get activeBackground() {
    return this.querySelector("[".concat(BG_ID_ATTR, "=\"").concat(this.activeBackgroundId, "\"]"));
  }

  get activeLayerIds() {
    return this.getAttribute(ACTIVE_LAYER_IDS_ATTR) || '';
  }

  set activeLayerIds(value) {
    if (value !== this.activeLayerIds) {
      if (value) {
        this.setAttribute(ACTIVE_LAYER_IDS_ATTR, value);
      } else {
        this.removeAttribute(ACTIVE_LAYER_IDS_ATTR);
      }
    }
  }

}

function _createIntersectionObserver2() {
  _classPrivateFieldSet(this, _observer, new ElementIntersectionObserver(this.triggerElements, _classPrivateFieldGet(this, _handleTriggerChange), {
    root: this.viewportIntersectionRoot,
    rootMargin: this.rootMargin
  }));
}

function _disconnectIntersectionObserver2() {
  if (_classPrivateFieldGet(this, _observer)) {
    _classPrivateFieldGet(this, _observer).disconnect();
  }
}

function _prepareBackgrounds2() {
  var viewportPosition = this.viewportPosition;

  if (viewportPosition === VIEWPORT_POSITIONS.aboveViewport || viewportPosition === VIEWPORT_POSITIONS.belowViewport) {
    var triggerElements = this.triggerElements;

    if (triggerElements.length > 0) {
      var triggerIndex = viewportPosition === VIEWPORT_POSITIONS.aboveViewport ? triggerElements.length - 1 : 0;
      this.activeBackgroundId = triggerElements[triggerIndex].getAttribute(SHOW_BACKGROUND_ATTR);
    }
  }
}

function _updateBackgrounds2() {
  var activeBackgroundId = this.activeBackgroundId;
  var backgrounds = this.backgrounds;
  backgrounds.forEach((background, idx) => {
    var backgroundId = background.getAttribute(BG_ID_ATTR);
    var isActive = backgroundId === activeBackgroundId;
    var nextActive = _classPrivateMethodGet(this, _getBackgroundId, _getBackgroundId2).call(this, backgrounds, idx + 1) === activeBackgroundId;
    var prevActive = _classPrivateMethodGet(this, _getBackgroundId, _getBackgroundId2).call(this, backgrounds, idx - 1) === activeBackgroundId;
    var state;

    if (isActive) {
      state = 'ACTIVE';
    } else if (nextActive) {
      state = 'PREV';
    } else if (prevActive) {
      state = 'NEXT';
    }

    var currentState = background.getAttribute(STATE_ATTR);

    if (state !== currentState) {
      setAttribute(background, STATE_ATTR, state);
    }
  });
}

function _getBackgroundId2(backgrounds, index) {
  if (backgrounds[index]) {
    return backgrounds[index].getAttribute(BG_ID_ATTR);
  }
}

function _updateLayers2() {
  var activeLayerIds = this.activeLayerIds.replace(/[[\]]/g, '').split(',');
  this.layers.forEach(layer => {
    var layerId = layer.getAttribute(LAYER_ID_ATTR);
    var isActive = activeLayerIds.indexOf(layerId) !== -1;
    setAttribute(layer, STATE_ATTR, isActive ? 'ACTIVE' : undefined);
  });
}

export { Scrollhistorie as default };
//# sourceMappingURL=dh-basis-scrollhistorie.esm.js.map
